import { useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ArrowRight, Mail } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { useI18n } from "@/i18n";
import { trackLead, trackReserveClick } from "@/lib/analytics";

/**
 * Unified thank-you page used after ANY form submission (private-event
 * quote request or contact message). Fires a SINGLE Meta Pixel "Lead"
 * event and a SINGLE GA4 "generate_lead" event on mount — so Meta Ads
 * and Google Ads only need one conversion configured for all leads.
 *
 * URL: /merci?form=events|contact&type=...&guests=...&subject=...
 */
export default function Merci() {
  const { t, lang, localePath, alternateUrls } = useI18n();
  const [params] = useSearchParams();
  const formName = params.get("form") || "contact";
  const isEvents = formName === "events";
  const eventType = params.get("type") || "";
  const guests = params.get("guests") || "";
  const subject = params.get("subject") || "";

  const ns = isEvents ? "events" : "contact";
  useSEO({
    title: t(`${ns}.thanks.title1`) + " " + t(`${ns}.thanks.title2`),
    description: t(`${ns}.thanks.body`),
    lang,
    alternates: alternateUrls(),
    noindex: true,
  });

  // The shared quote form already fires the Lead event with its page_source,
  // so skip it here when arriving with lead=1 to avoid a duplicate.
  const leadAlreadyFired = params.get("lead") === "1";

  const fired = useRef(false);
  useEffect(() => {
    if (fired.current || leadAlreadyFired) return;
    fired.current = true;
    trackLead({
      form_name: formName,
      content_category: isEvents ? "private_event" : "contact",
      event_type: eventType,
      guests,
      subject,
    });
  }, [formName, isEvents, eventType, guests, subject, leadAlreadyFired]);

  return (
    <div data-testid="thanks-page" className="px-6 md:px-12 lg:px-16">
      <section className="py-24 md:py-32 max-w-2xl mx-auto text-center">
        <CheckCircle2 size={56} className="text-[#B8956A] mx-auto" />
        <p className="overline mt-8">{t(`${ns}.thanks.overline`)}</p>
        <h1 className="font-display text-5xl md:text-6xl mt-4 leading-[0.95]">
          {t(`${ns}.thanks.title1`)}<br />
          <span className="italic text-[#B8956A]">{t(`${ns}.thanks.title2`)}</span>
        </h1>
        <p className="text-[#697585] mt-8 text-lg max-w-xl mx-auto leading-relaxed">
          {t(`${ns}.thanks.body`)}
        </p>

        <div className="mt-8 inline-flex items-center gap-3 text-sm text-[#697585]">
          <Mail size={16} className="text-[#B8956A]" />
          {t(`${ns}.thanks.checkEmail`)}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to={localePath("/")}
            data-testid="thanks-back-home"
            className="btn-ghost justify-center"
          >
            {t(`${ns}.thanks.backHome`)}
          </Link>
          {isEvents ? (
            <Link
              to={localePath("/menu")}
              data-testid="thanks-secondary-cta"
              className="btn-primary justify-center"
            >
              {t("events.thanks.seeMenu")} <ArrowRight size={14} />
            </Link>
          ) : (
            <Link
              to={localePath("/reservation")}
              data-testid="thanks-secondary-cta"
              onClick={() => trackReserveClick("thanks_page")}
              className="btn-primary justify-center"
            >
              {t("contact.thanks.reserve")} <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </section>
    </div>
  );
}
